import { useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Download,
  FileSpreadsheet,
  LoaderCircle,
  Upload,
} from "lucide-react";
import { useReOS } from "./domain/store";
import {
  conditionLabels,
  inventoryStatusLabels,
  marketplaceLabels,
  saleStatusLabels,
} from "./domain/labels";
import { formatMoney } from "./domain/money";
import { readSpreadsheetFile } from "./domain/spreadsheetFile";
import {
  parseSpreadsheetText,
  type SpreadsheetSheet,
} from "./domain/spreadsheetImport";
import { useSpreadsheetDraft } from "./domain/spreadsheetDraft";
import {
  migrationFields,
  migrationReportCSV,
  migrationStatuses,
  suggestMigrationHeader,
  suggestMigrationMapping,
  type MigrationDecision,
  type MigrationField,
  type MigrationJob,
  type MigrationOptions,
  type MigrationPage,
  type MigrationReviewRow,
  type MigrationSheet,
  type MigrationSourceRow,
} from "./domain/spreadsheetMigration";
import { Button } from "./components/ui/button";
import { Alert } from "./components/ui/alert";
import {
  Field,
  FieldGroup,
  Input,
  Textarea,
  CheckboxField,
} from "./components/ui/field";
import { SelectControl } from "./components/ui/select";
import { Disclosure } from "./components/ui/disclosure";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./components/ui/tabs";
import "./spreadsheet-migration.css";
interface UploadDraft {
  fileName: string;
  sheets: SpreadsheetSheet[];
  selected: boolean[];
  headers: number[];
  mappings: MigrationField[][];
  defaults: MigrationSheet["defaultStatus"][];
  options: MigrationOptions;
  jobID?: string;
}
const fresh = (): UploadDraft => ({
  fileName: "",
  sheets: [],
  selected: [],
  headers: [],
  mappings: [],
  defaults: [],
  options: {
    source: "My stock records",
    currency: "GBP",
    dateOrder: "dmy",
    numberStyle: "dot",
    costBasis: "unit",
    stockAsOf: new Date().toISOString().slice(0, 10),
  },
});
const accepts = (value: unknown): value is UploadDraft =>
  Boolean(
    value &&
    typeof value === "object" &&
    Array.isArray((value as UploadDraft).sheets) &&
    (value as UploadDraft).options,
  );
const fileHash = async (value: unknown) =>
  [
    ...new Uint8Array(
      await crypto.subtle.digest(
        "SHA-256",
        new TextEncoder().encode(JSON.stringify(value)),
      ),
    ),
  ]
    .map((v) => v.toString(16).padStart(2, "0"))
    .join("");
const errorText = (error: unknown) =>
  error instanceof Error
    ? error.message
    : "The import could not finish. Reload your saved progress and try again.";
export function SpreadsheetMigration({ onBack }: { onBack: () => void }) {
  const store = useReOS();
  return (
    <MigrationWorkspace
      key={`${store.user?.id}:${store.workspace?.id}`}
      onBack={onBack}
    />
  );
}
function MigrationWorkspace({ onBack }: { onBack: () => void }) {
  const store = useReOS(),
    storeRef = useRef(store);
  storeRef.current = store;
  const draft = useSpreadsheetDraft(
    `migration:v1:${store.user?.id}:${store.workspace?.id}`,
    fresh,
    accepts,
  );
  const [jobs, setJobs] = useState<MigrationJob[]>([]),
    [page, setPage] = useState<MigrationPage>();
  const [stage, setStage] = useState<
    "entry" | "columns" | "match" | "review" | "receipt"
  >("entry");
  const [busy, setBusy] = useState(""),
    [error, setError] = useState(""),
    [notice, setNotice] = useState("");
  const [paste, setPaste] = useState(""),
    [active, setActive] = useState(""),
    [filter, setFilter] = useState("attention");
  const [resuming, setResuming] = useState<MigrationJob>();
  const [pendingChoice, setPendingChoice] = useState(false),
    [discarding, setDiscarding] = useState(false);
  const working = useRef(false);
  const fileInput = useRef<HTMLInputElement>(null),
    heading = useRef<HTMLHeadingElement>(null),
    mounted = useRef(true),
    pause = useRef(false);
  const current = draft.value;
  useEffect(() => {
    mounted.current = true;
    void storeRef.current
      .spreadsheetRequest<{
        jobs: MigrationJob[];
      }>({ action: "list" })
      .then((r) => {
        if (mounted.current) setJobs(r.jobs);
      })
      .catch((e) => {
        if (mounted.current) setError(errorText(e));
      });
    return () => {
      mounted.current = false;
      pause.current = true;
    };
  }, []);
  useEffect(() => {
    heading.current?.focus();
  }, [stage]);
  const act = async (label: string, operation: () => Promise<void>) => {
    if (working.current) return;
    working.current = true;
    setBusy(label);
    setError("");
    try {
      await operation();
    } catch (e) {
      if (mounted.current) setError(errorText(e));
    } finally {
      working.current = false;
      if (mounted.current) setBusy("");
    }
  };
  const load = async (id: string, target: typeof stage = "match") => {
    const result = await store.spreadsheetRequest<MigrationPage>({
      action: "read",
      jobID: id,
    });
    setPage(result);
    setResuming(result.job.status === "uploading" ? result.job : undefined);
    setActive(
      result.rows.find((r) => r.status === "attention")?.id ??
        result.rows[0]?.id ??
        "",
    );
    setFilter(result.counts.attention ? "attention" : "all");
    setStage(result.job.status === "uploading" ? "entry" : target);
    return result;
  };
  const choose = (sheets: SpreadsheetSheet[], name: string) => {
    if (resuming) {
      const selected = resuming.sheets.map((saved) =>
        sheets.find((sheet) => sheet.name === saved.name),
      );
      const headers = selected.map(
        (sheet, i) =>
          sheet?.rows
            .slice(0, 20)
            .findIndex(
              (row) =>
                JSON.stringify(row) ===
                JSON.stringify(resuming.sheets[i].headers),
            ) ?? -1,
      );
      if (
        selected.some((sheet) => !sheet) ||
        headers.some((index) => index < 0)
      )
        throw new Error(
          "Choose the original spreadsheet to finish this upload. Its worksheet names and headings must match.",
        );
      draft.setValue({
        ...fresh(),
        jobID: resuming.id,
        options: resuming.options,
        fileName: resuming.fileName,
        sheets: selected as SpreadsheetSheet[],
        headers,
        selected: selected.map(() => true),
        mappings: resuming.sheets.map((sheet) => sheet.mapping),
        defaults: resuming.sheets.map((sheet) => sheet.defaultStatus),
      });
    } else {
      const headers = sheets.map((s) => suggestMigrationHeader(s.rows));
      draft.setValue({
        ...fresh(),
        options: {
          ...current.options,
          costBasis: sheets.some((sheet, i) =>
            sheet.rows[headers[i]].some((label) =>
              /\b(lot cost|total purchase cost)\b/i.test(label),
            ),
          )
            ? "lot"
            : "unit",
          currency: store.workspace?.defaultCurrency ?? "GBP",
        },
        fileName: name,
        sheets,
        headers,
        selected: sheets.map(() => true),
        mappings: sheets.map((s, i) =>
          suggestMigrationMapping(s.rows[headers[i]]),
        ),
        defaults: sheets.map((sheet, i) => {
          const fields = suggestMigrationMapping(sheet.rows[headers[i]]);
          return fields.includes("sale_price") && fields.includes("sold_at")
            ? "completed"
            : "stock";
        }),
      });
    }
    setStage("columns");
  };
  const readFile = (file: File) =>
    act("Reading spreadsheet…", async () =>
      choose(await readSpreadsheetFile(file), file.name),
    );
  const upload = async () => {
    const chosen = current.sheets
      .map((sheet, i) => ({ sheet, i }))
      .filter(({ i }) => current.selected[i]);
    if (!chosen.length) throw new Error("Choose at least one worksheet.");
    const sheets: MigrationSheet[] = chosen.map(({ sheet, i }) => ({
      name: sheet.name,
      headers: sheet.rows[current.headers[i]],
      mapping: current.mappings[i],
      defaultStatus: current.defaults[i],
    }));
    const rows: MigrationSourceRow[] = chosen.flatMap(({ sheet, i }, si) =>
      sheet.rows.slice(current.headers[i] + 1).flatMap((cells, ri) =>
        cells.some((c) => c.trim()) ||
        sheet.formulaCells?.some(
          (key) => Number(key.split(":")[0]) === current.headers[i] + ri + 1,
        )
          ? [
              {
                sheet: si,
                row: current.headers[i] + ri + 2,
                cells,
                formulaColumns: (sheet.formulaCells ?? [])
                  .filter(
                    (key) =>
                      Number(key.split(":")[0]) === current.headers[i] + ri + 1,
                  )
                  .map((key) => Number(key.split(":")[1])),
                warnings: Object.fromEntries(
                  Object.entries(sheet.cellWarnings ?? {}).filter(
                    ([key]) =>
                      Number(key.split(":")[0]) === current.headers[i] + ri + 1,
                  ),
                ),
              },
            ]
          : [],
      ),
    );
    if (!rows.length || rows.length > 5000)
      throw new Error("Choose between 1 and 5,000 records for this import.");
    const id =
      current.jobID ?? `sheet_${crypto.randomUUID().replace(/-/g, "")}`;
    draft.setValue((old) => ({ ...old, jobID: id }));
    await draft.flush();
    let result = await store.spreadsheetRequest<MigrationPage>({
      action: "create",
      job: {
        id,
        fileName: current.fileName,
        fingerprint: await fileHash(rows),
        expectedRows: rows.length,
        sheets,
        options: current.options,
      },
    });
    for (let offset = 0; offset < rows.length; offset += 50) {
      if (!mounted.current) return;
      setBusy(
        `Saving source rows ${Math.min(offset + 50, rows.length)} of ${rows.length}…`,
      );
      result = await store.spreadsheetRequest<MigrationPage>({
        action: "upload",
        jobID: id,
        rows: rows.slice(offset, offset + 50),
      });
    }
    setPage(result);
    setFilter(result.counts.attention ? "attention" : "all");
    setActive(
      result.rows.find((r) => r.status === "attention")?.id ??
        result.rows[0]?.id ??
        "",
    );
    setStage("match");
    setResuming(undefined);
    draft.setValue(fresh());
    await draft.flush();
  };
  const decide = async (
    row: MigrationReviewRow,
    decision: MigrationDecision,
  ) => {
    const result = await store.spreadsheetRequest<MigrationPage>({
      action: "decide",
      jobID: page!.job.id,
      revision: page!.job.revision,
      decisions: [{ id: row.id, decision }],
    });
    setPage(result);
    const next =
      result.rows.find((r) => r.id === row.id && r.status === "attention") ??
      result.rows.find((r) => r.id !== row.id && r.status === "attention");
    setActive(next?.id ?? row.id);
    if (!result.counts.attention) setFilter("all");
    setNotice("Choice saved to your workspace.");
    if (result.job.status === "complete") setStage("receipt");
  };
  const commit = async () => {
    pause.current = false;
    let latest = page!;
    while (!pause.current && mounted.current) {
      const ready = latest.rows.filter((r) =>
        ["ready", "unchanged"].includes(r.status),
      );
      if (!ready.length) break;
      const groups = new Map<string, MigrationReviewRow[]>();
      for (const row of ready)
        groups.set(row.group, [...(groups.get(row.group) ?? []), row]);
      const group: MigrationReviewRow[] = [];
      for (const rows of groups.values()) {
        if (rows.length > 100)
          throw new Error(
            "This item has more than 100 related rows. Leave out older rows and import them in a separate file.",
          );
        if (group.length + rows.length > 100) break;
        group.push(...rows);
      }
      setBusy(
        `Importing · ${latest.counts.saved} of ${latest.rows.length} records saved`,
      );
      try {
        latest = await store.spreadsheetRequest<MigrationPage>({
          action: "commit",
          jobID: latest.job.id,
          rows: group.map(({ id, version }) => ({ id, version })),
        });
      } catch (e) {
        // A connection can drop after the transaction commits. Read the durable
        // receipts before presenting failure or offering to send another batch.
        const recovered = await store.spreadsheetRequest<MigrationPage>({
          action: "read",
          jobID: latest.job.id,
        });
        setPage(recovered);
        if (
          group.every((row) =>
            recovered.rows.some(
              (saved) => saved.id === row.id && saved.receipt,
            ),
          )
        ) {
          latest = recovered;
          continue;
        }
        throw e;
      }
      if (mounted.current) setPage(latest);
    }
    if (mounted.current) {
      setStage("receipt");
      await store.refreshWorkspace();
    }
  };
  const download = () => {
    if (!page) return;
    const url = URL.createObjectURL(
      new Blob([migrationReportCSV(page)], { type: "text/csv;charset=utf-8" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = "listwork-import-report.csv";
    link.click();
    URL.revokeObjectURL(url);
  };
  const visible =
    page?.rows.filter((row) => filter === "all" || row.status === filter) ?? [];
  const row = visible.find((r) => r.id === active) ?? visible[0];
  const index = row ? visible.findIndex((r) => r.id === row.id) : 0;
  const option = <K extends keyof MigrationOptions>(
    key: K,
    value: MigrationOptions[K],
  ) =>
    draft.setValue((d) => ({ ...d, options: { ...d.options, [key]: value } }));
  const titles = {
    entry: "Import your spreadsheet",
    columns: "Check your columns",
    match: "Match your records",
    review: "Review the import",
    receipt:
      page?.job.status === "complete"
        ? "Import complete"
        : "Your import progress",
  };
  return (
    <section className="migration-workspace" aria-busy={Boolean(busy)}>
      <header className="migration-header">
        <Button
          variant="ghost"
          onClick={onBack}
          disabled={Boolean(busy) || pendingChoice}
        >
          <ArrowLeft />
          Inventory
        </Button>
        <span>
          {page
            ? "Saved to your workspace"
            : current.sheets.length
              ? draft.dirty
                ? "Saving on this device…"
                : "Saved on this device"
              : ""}
        </span>
      </header>
      <h1 ref={heading} tabIndex={-1}>
        {titles[stage]}
      </h1>
      {(error || draft.error) && (
        <Alert tone="danger">
          <p>{error || draft.error}</p>
          {page && (
            <Button
              variant="secondary"
              disabled={Boolean(busy)}
              onClick={() =>
                void act("Reloading saved progress…", async () => {
                  await load(page.job.id);
                })
              }
            >
              Reload saved progress
            </Button>
          )}
        </Alert>
      )}
      {busy && (
        <p className="migration-progress" role="status">
          <LoaderCircle className="spinner" />
          {busy}
        </p>
      )}
      <p className="sr-only" role="status">
        {notice}
      </p>

      {stage === "entry" && (
        <div className="migration-entry">
          <p>
            Bring in stock, listings and sales history. Review matches with your
            existing records before anything is saved.
          </p>
          {resuming && (
            <Alert tone="info">
              <p>
                Finish uploading {resuming.fileName}. Choose the original file
                to restore your saved column choices.
              </p>
              <Button
                variant="ghost"
                disabled={Boolean(busy)}
                onClick={() => {
                  setResuming(undefined);
                  setPage(undefined);
                }}
              >
                Start a different import
              </Button>
            </Alert>
          )}
          <input
            ref={fileInput}
            hidden
            type="file"
            accept=".xlsx,.csv,.tsv"
            aria-label="Upload management spreadsheet"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void readFile(file);
              e.target.value = "";
            }}
          />
          <Button
            variant="primary"
            disabled={Boolean(busy) || !draft.ready}
            onClick={() => fileInput.current?.click()}
          >
            <Upload />
            Choose spreadsheet
          </Button>
          <small>Excel (.xlsx), CSV or TSV · up to 5 MB and 5,000 rows</small>
          {current.sheets.length > 0 && (
            <div className="migration-resume">
              <FileSpreadsheet />
              <div>
                <strong>{current.fileName}</strong>
                <p>
                  {current.jobID
                    ? "Continue saving your source rows"
                    : "Column choices saved on this device"}
                </p>
              </div>
              <Button
                variant="secondary"
                disabled={Boolean(busy)}
                onClick={() =>
                  current.jobID
                    ? void act("Loading saved import…", async () => {
                        const saved = await load(current.jobID!);
                        if (saved.job.status === "uploading") await upload();
                      })
                    : setStage("columns")
                }
              >
                Continue
              </Button>
            </div>
          )}
          <Disclosure label="Paste cells instead">
            <Field label="Spreadsheet cells">
              <Textarea
                value={paste}
                onChange={(e) => setPaste(e.target.value)}
                rows={6}
              />
            </Field>
            <Button
              variant="secondary"
              disabled={!paste.trim() || Boolean(busy)}
              onClick={() =>
                void act("Reading cells…", async () =>
                  choose(
                    [
                      {
                        name: "Pasted cells",
                        rows: parseSpreadsheetText(paste),
                      },
                    ],
                    "Pasted cells",
                  ),
                )
              }
            >
              Use these cells
            </Button>
          </Disclosure>
          {jobs.length > 0 && (
            <section className="migration-recent">
              <h2>Your imports</h2>
              {jobs.map((job) => (
                <button
                  key={job.id}
                  onClick={() =>
                    void act("Loading import…", async () => {
                      await load(
                        job.id,
                        job.status === "complete" ? "receipt" : "match",
                      );
                    })
                  }
                  disabled={Boolean(busy)}
                >
                  <span>
                    <strong>{job.fileName}</strong>
                    <small>
                      {job.options.source} · {job.createdAt.slice(0, 10)}
                    </small>
                  </span>
                  <span>
                    {job.status === "complete" ? "View receipt" : "Continue"}
                    <ArrowRight />
                  </span>
                </button>
              ))}
            </section>
          )}
        </div>
      )}
      {stage === "columns" && (
        <>
          <p>
            Match each column to its meaning in Listwork. Leave summary tabs and
            unused columns out.
          </p>
          <fieldset
            className="migration-fields"
            disabled={Boolean(busy) || Boolean(current.jobID)}
          >
            <FieldGroup className="migration-options">
              <Field
                label="Name for these records"
                helper="Reuse this name when importing an updated copy."
              >
                <Input
                  value={current.options.source}
                  onChange={(e) => option("source", e.target.value)}
                />
              </Field>
              <Field label="Stock counts are current as of">
                <Input
                  type="date"
                  max={new Date().toISOString().slice(0, 10)}
                  value={current.options.stockAsOf}
                  onChange={(e) => option("stockAsOf", e.target.value)}
                />
              </Field>
            </FieldGroup>
            {current.sheets.map((sheet, si) => (
              <section className="migration-sheet" key={si}>
                <CheckboxField
                  checked={current.selected[si]}
                  onChange={(e) =>
                    draft.setValue((d) => ({
                      ...d,
                      selected: d.selected.map((v, i) =>
                        i === si ? e.target.checked : v,
                      ),
                    }))
                  }
                  label={<>{sheet.name}</>}
                />
                {current.selected[si] && (
                  <Disclosure
                    label={`Check ${sheet.rows[current.headers[si]].length} columns`}
                    open={si === 0}
                  >
                    <FieldGroup className="migration-options">
                      <Field label={`Header row for ${sheet.name}`}>
                        <SelectControl
                          value={String(current.headers[si])}
                          onValueChange={(value) => {
                            const n = Number(value);
                            draft.setValue((d) => ({
                              ...d,
                              headers: d.headers.map((v, i) =>
                                i === si ? n : v,
                              ),
                              mappings: d.mappings.map((v, i) =>
                                i === si
                                  ? suggestMigrationMapping(sheet.rows[n])
                                  : v,
                              ),
                            }));
                          }}
                          options={[
                            ...sheet.rows.slice(0, 20).map((_, i) => ({
                              value: String(i),
                              label: <>Row {i + 1}</>,
                            })),
                          ]}
                        />
                      </Field>
                      <Field label={`When status is blank in ${sheet.name}`}>
                        <SelectControl
                          value={current.defaults[si]}
                          onValueChange={(value) =>
                            draft.setValue((d) => ({
                              ...d,
                              defaults: d.defaults.map((v, i) =>
                                i === si
                                  ? (value as MigrationSheet["defaultStatus"])
                                  : v,
                              ),
                            }))
                          }
                          options={[
                            ...migrationStatuses.map(([key, label]) => ({
                              value: String(key),
                              label: <>{label}</>,
                            })),
                          ]}
                        />
                      </Field>
                    </FieldGroup>
                    <div className="migration-columns">
                      {sheet.rows[current.headers[si]].map((label, ci) => (
                        <div key={ci}>
                          <div>
                            <strong>{label || `Column ${ci + 1}`}</strong>
                            <small>
                              {sheet.rows[current.headers[si] + 1]?.[ci] ||
                                "Empty in first row"}
                            </small>
                          </div>
                          <Field
                            label={`Use ${label || `column ${ci + 1}`} as`}
                          >
                            <SelectControl
                              value={current.mappings[si][ci]}
                              onValueChange={(value) =>
                                draft.setValue((d) => ({
                                  ...d,
                                  mappings: d.mappings.map((mapping, i) =>
                                    i === si
                                      ? mapping.map((v, col) =>
                                          col === ci
                                            ? (value as MigrationField)
                                            : v,
                                        )
                                      : mapping,
                                  ),
                                }))
                              }
                              options={[
                                ...migrationFields.map(([key, title]) => ({
                                  value: String(key),
                                  label: <>{title}</>,
                                })),
                              ]}
                            />
                          </Field>
                        </div>
                      ))}
                    </div>
                  </Disclosure>
                )}
              </section>
            ))}
            {current.sheets.some(
              (sheet, i) => current.selected[i] && sheet.formulaCells?.length,
            ) && (
              <Alert tone="warning">
                <p>
                  This workbook contains formulas. Listwork uses the results
                  saved in the file.
                </p>
                <CheckboxField
                  label="I recalculated and saved the workbook before uploading it"
                  checked={current.options.formulaValuesConfirmed === true}
                  onChange={(event) =>
                    option("formulaValuesConfirmed", event.target.checked)
                  }
                />
              </Alert>
            )}
            <Disclosure label="Amounts and dates" open>
              <FieldGroup className="migration-options">
                <Field label="Purchase costs are">
                  <SelectControl
                    value={current.options.costBasis}
                    onValueChange={(value) =>
                      option("costBasis", value as "unit" | "lot")
                    }
                    options={[
                      { value: "unit", label: <>Per unit</> },
                      {
                        value: "lot",
                        label: <>Whole lot (map the number of units)</>,
                      },
                    ]}
                  />
                </Field>
                <Field label="Dates are">
                  <SelectControl
                    value={current.options.dateOrder}
                    onValueChange={(value) =>
                      option(
                        "dateOrder",
                        value as MigrationOptions["dateOrder"],
                      )
                    }
                    options={[
                      { value: "dmy", label: <>Day / month / year</> },
                      { value: "mdy", label: <>Month / day / year</> },
                      {
                        value: "auto",
                        label: <>Detect (ask when ambiguous)</>,
                      },
                    ]}
                  />
                </Field>
                <Field label="Number format">
                  <SelectControl
                    value={current.options.numberStyle}
                    onValueChange={(value) =>
                      option(
                        "numberStyle",
                        value as MigrationOptions["numberStyle"],
                      )
                    }
                    options={[
                      { value: "dot", label: <>1,234.56</> },
                      { value: "comma", label: <>1.234,56</> },
                      { value: "auto", label: <>Detect</> },
                    ]}
                  />
                </Field>
                <Field label="Currency">
                  <Input readOnly value={current.options.currency} />
                </Field>
              </FieldGroup>
              <p>
                Sale amounts cover the whole order line. Blank transaction
                quantity means one unit. Available quantity is a separate
                current stock count. Refunds are the total refunded for the
                sale, including refunded postage.
              </p>
            </Disclosure>
          </fieldset>
          <footer className="migration-footer">
            <Button
              variant="secondary"
              onClick={() => setStage("entry")}
              disabled={Boolean(busy)}
            >
              Back
            </Button>
            <Button
              variant="primary"
              disabled={Boolean(busy) || draft.invalidated}
              onClick={() => void act("Saving your source records…", upload)}
            >
              Find matches
              <ArrowRight />
            </Button>
          </footer>
        </>
      )}
      {stage === "match" && page && (
        <>
          <div className="migration-review-heading">
            <p>
              {page.job.fileName} · {page.rows.length} records
            </p>
            <Button
              variant="secondary"
              disabled={Boolean(busy) || pendingChoice}
              onClick={() => setStage("review")}
            >
              Review import
              <ArrowRight />
            </Button>
          </div>
          {pendingChoice && (
            <p role="status">
              Save or discard your choice before moving to another record.
            </p>
          )}
          <Tabs
            value={filter}
            onValueChange={(value) => {
              if (!pendingChoice && !busy) setFilter(value);
            }}
          >
            <TabsList aria-label="Import records">
              <TabsTrigger
                disabled={pendingChoice || Boolean(busy)}
                value="attention"
              >
                Needs review ({page.counts.attention})
              </TabsTrigger>
              <TabsTrigger
                disabled={pendingChoice || Boolean(busy)}
                value="ready"
              >
                Ready ({page.counts.ready})
              </TabsTrigger>
              <TabsTrigger
                disabled={pendingChoice || Boolean(busy)}
                value="all"
              >
                All ({page.rows.length})
              </TabsTrigger>
            </TabsList>
            <TabsContent value={filter}>
              {row ? (
                <div className="migration-workbench">
                  <nav
                    aria-label="Spreadsheet records"
                    className="migration-queue"
                  >
                    {visible.map((r) => (
                      <button
                        key={r.id}
                        aria-current={r.id === row.id ? "true" : undefined}
                        disabled={pendingChoice || Boolean(busy)}
                        onClick={() => setActive(r.id)}
                      >
                        <strong>{r.title}</strong>
                        <small>
                          {r.sheet} · row {r.row} ·{" "}
                          {r.status === "attention" ? "Needs review" : r.status}
                        </small>
                      </button>
                    ))}
                  </nav>
                  <div className="migration-record">
                    <div className="migration-record-nav">
                      <Button
                        variant="ghost"
                        aria-label="Previous record"
                        disabled={index === 0 || Boolean(busy) || pendingChoice}
                        onClick={() => setActive(visible[index - 1].id)}
                      >
                        <ArrowLeft />
                      </Button>
                      <span>
                        {index + 1} of {visible.length}
                      </span>
                      <Button
                        variant="ghost"
                        aria-label="Next record"
                        disabled={
                          index === visible.length - 1 ||
                          Boolean(busy) ||
                          pendingChoice
                        }
                        onClick={() => setActive(visible[index + 1].id)}
                      >
                        <ArrowRight />
                      </Button>
                    </div>
                    <RecordReview
                      key={`${row.id}:${page.job.revision}`}
                      row={row}
                      currency={page.job.options.currency}
                      busy={Boolean(busy)}
                      onDirty={setPendingChoice}
                      onSave={(d) =>
                        void act("Saving choice…", () => decide(row, d))
                      }
                    />
                  </div>
                </div>
              ) : (
                <div className="migration-empty">
                  <Check />
                  <h2>
                    {filter === "attention"
                      ? "No matches need your attention"
                      : "No records in this view"}
                  </h2>
                  <Button
                    variant="secondary"
                    onClick={() => setStage("review")}
                  >
                    Review import
                  </Button>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </>
      )}
      {stage === "review" && page && (
        <>
          <div className="migration-summary">
            <h2>{page.counts.ready + page.counts.unchanged} records ready</h2>
            <ul className="migration-outcomes">
              {(
                [
                  [page.counts.newItems, "new item", "new items"],
                  [page.counts.updatedItems, "updated item", "updated items"],
                  [page.counts.listings, "listing record", "listing records"],
                  [page.counts.sales, "sale", "sales"],
                  [page.counts.events, "history event", "history events"],
                ] as const
              )
                .filter(([count]) => count > 0)
                .map(([count, one, many]) => (
                  <li key={one}>
                    <strong>{count}</strong> {count === 1 ? one : many}
                  </li>
                ))}
            </ul>
            <p>
              {page.counts.stockChanges
                ? `${page.counts.stockChanges} opening stock counts will be saved.`
                : "Current stock stays as it is."}{" "}
              Marketplace listings will not be published or edited.
            </p>
          </div>
          {page.counts.attention > 0 && (
            <Alert tone="warning">
              {page.counts.attention} records still need review. Their related
              items will stay out of this import.
              <Button
                variant="ghost"
                onClick={() => {
                  setFilter("attention");
                  setStage("match");
                }}
              >
                Resolve records
              </Button>
            </Alert>
          )}
          <ImportImpact
            rows={page.rows.filter(
              (r) => r.status === "ready" || r.status === "unchanged",
            )}
          />
          <div className="migration-change-list">
            {page.rows
              .filter((r) => r.status === "ready" || r.status === "unchanged")
              .map((r) => (
                <Disclosure
                  key={r.id}
                  label={`${r.title} · ${r.sheet}, row ${r.row}`}
                >
                  <Changes changes={r.changes} />
                  <small>
                    {r.sheet} · row {r.row}
                  </small>
                </Disclosure>
              ))}
          </div>
          <footer className="migration-footer">
            <Button
              variant="secondary"
              disabled={Boolean(busy)}
              onClick={() => setStage("match")}
            >
              Back to matches
            </Button>
            {busy ? (
              <Button
                variant="secondary"
                onClick={() => {
                  pause.current = true;
                  setNotice("Pausing after the current batch is saved.");
                }}
              >
                Pause import
              </Button>
            ) : (
              <Button
                variant="primary"
                disabled={
                  page.job.status !== "complete" &&
                  !page.counts.ready &&
                  !page.counts.unchanged
                }
                onClick={() =>
                  page.job.status === "complete"
                    ? setStage("receipt")
                    : void act("Starting import…", commit)
                }
              >
                {page.job.status === "complete"
                  ? "View receipt"
                  : `Import ${page.counts.ready + page.counts.unchanged} records`}
                <ArrowRight />
              </Button>
            )}
          </footer>
        </>
      )}
      {stage === "receipt" && page && (
        <div className="migration-receipt">
          <Check />
          <h2>{page.counts.saved} records saved</h2>
          <p>
            {page.counts.attention + page.counts.ready + page.counts.unchanged}{" "}
            left to finish · {page.counts.skipped} left out
          </p>
          <div className="migration-actions">
            <Button variant="primary" onClick={onBack}>
              View inventory
              <ArrowRight />
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setFilter(page.counts.attention ? "attention" : "all");
                setStage("match");
              }}
            >
              {page.job.status === "complete"
                ? "Review records"
                : "Continue import"}
            </Button>
            <Button variant="ghost" onClick={download}>
              <Download />
              Download report
            </Button>
          </div>
        </div>
      )}
      {page && page.counts.saved === 0 && (
        <div className="migration-discard">
          {discarding ? (
            <Alert tone="warning">
              <p>
                Discard this unfinished import? No inventory records have been
                saved from it.
              </p>
              <Button
                variant="ghost"
                disabled={Boolean(busy)}
                onClick={() => setDiscarding(false)}
              >
                Keep import
              </Button>
              <Button
                variant="danger-outline"
                disabled={Boolean(busy)}
                onClick={() =>
                  void act("Discarding import…", async () => {
                    await store.spreadsheetRequest({
                      action: "delete",
                      jobID: page.job.id,
                    });
                    setJobs((jobs) =>
                      jobs.filter((job) => job.id !== page.job.id),
                    );
                    if (current.jobID === page.job.id) draft.setValue(fresh());
                    setPage(undefined);
                    setResuming(undefined);
                    setDiscarding(false);
                    setStage("entry");
                  })
                }
              >
                Discard import
              </Button>
            </Alert>
          ) : (
            <Button
              variant="ghost"
              disabled={Boolean(busy) || pendingChoice}
              onClick={() => setDiscarding(true)}
            >
              Discard unfinished import
            </Button>
          )}
        </div>
      )}
    </section>
  );
}
function Changes({ changes }: { changes: MigrationReviewRow["changes"] }) {
  return (
    <dl className="migration-changes">
      {changes.map((c) => (
        <div key={c.field}>
          <dt>{c.label}</dt>
          <dd>
            <span>{c.before}</span>
            <ArrowRight />
            <strong>{c.selected ? c.after : `Keep ${c.before}`}</strong>
          </dd>
        </div>
      ))}
    </dl>
  );
}
const financialFields = new Set([
  "item.purchaseCost",
  "item.targetPrice",
  "sale.salePrice",
  "sale.shippingCharged",
  "sale.marketplaceFees",
  "sale.paymentProcessingFees",
  "sale.shippingCost",
  "sale.packagingCost",
  "sale.purchaseCost",
  "sale.refundAmount",
  "sale.quantity",
]);
function ImportImpact({ rows }: { rows: MigrationReviewRow[] }) {
  const groups = new Map<string, MigrationReviewRow[]>();
  for (const row of rows)
    groups.set(row.group, [...(groups.get(row.group) ?? []), row]);
  const impacts = [...groups.values()]
    .map((records) => {
      const changes = new Map<string, MigrationReviewRow["changes"][number]>();
      for (const row of records)
        for (const change of row.changes) {
          if (
            !change.selected ||
            !(
              change.field === "item.quantity" ||
              (change.conflict && financialFields.has(change.field))
            )
          )
            continue;
          const sale = change.field.startsWith("sale.");
          const key = `${change.field}:${change.before}:${change.after}:${sale ? (row.saleID ?? row.id) : ""}`;
          changes.set(key, {
            ...change,
            field: key,
            label: sale
              ? `${change.label} · ${row.sheet}, row ${row.row}`
              : change.label,
          });
        }
      return { records, changes: [...changes.values()] };
    })
    .filter((group) => group.changes.length);
  if (!impacts.length) return null;
  return (
    <section className="migration-impact" aria-label="Stock and money changes">
      <h2>Stock and money changes</h2>
      {impacts.map(({ records, changes }) => (
        <article key={records[0].group}>
          <h3>{records[0].itemTitle ?? records[0].title}</h3>
          <small>
            {records[0].sku ? `${records[0].sku} · ` : ""}
            {records.length > 1
              ? `${records.length} related records, one inventory item`
              : `${records[0].sheet}, row ${records[0].row}`}
          </small>
          <Changes changes={changes} />
        </article>
      ))}
    </section>
  );
}
function MatchFacts({
  kind,
  rows,
  photo,
}: {
  kind: "item" | "sale";
  rows: Array<
    [string, string | number | undefined, string | number | undefined]
  >;
  photo?: string;
}) {
  return (
    <table
      className="migration-comparison"
      aria-label={`${kind === "item" ? "Item" : "Sale"} comparison`}
    >
      <thead>
        <tr>
          <th scope="col">
            <span className="sr-only">Detail</span>
          </th>
          <th scope="col">Spreadsheet</th>
          <th scope="col">
            {photo && <img src={photo} alt="Existing inventory item" />}Listwork
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([label, source, current]) => (
          <tr key={label}>
            <th scope="row">{label}</th>
            <td>{source ?? "Not supplied"}</td>
            <td>{current ?? "Not recorded"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
function RecordReview({
  row,
  currency,
  busy,
  onDirty,
  onSave,
}: {
  row: MigrationReviewRow;
  currency: string;
  busy: boolean;
  onSave: (decision: MigrationDecision) => void;
  onDirty: (dirty: boolean) => void;
}) {
  const store = useReOS();
  const [decision, setDecision] = useState(row.decision),
    [field, setField] = useState<MigrationField>("status"),
    [value, setValue] = useState(
      row.decision.values?.status ?? row.sourceValues.status ?? "",
    );
  const [query, setQuery] = useState(""),
    [matches, setMatches] = useState(row.suggestions);
  const [searching, setSearching] = useState(false),
    [searchError, setSearchError] = useState("");
  const dirty =
    JSON.stringify(decision) !== JSON.stringify(row.decision) ||
    value !== (decision.values?.[field] ?? row.sourceValues[field] ?? "");
  useEffect(() => {
    onDirty(dirty);
    return () => onDirty(false);
  }, [dirty, onDirty]);
  const resolved = row.status === "saved";
  const itemChoices = [
    ...new Map(
      [...row.suggestions, ...matches].map((item) => [item.id, item]),
    ).values(),
  ];
  const candidateID = decision.createItem
    ? undefined
    : (decision.itemID ?? row.itemID);
  const candidate =
    itemChoices.find((item) => item.id === candidateID) ??
    store.inventoryItems.find((item) => item.id === candidateID);
  const possibleItem =
    candidate ??
    (!decision.createItem && itemChoices.length === 1
      ? itemChoices[0]
      : undefined);
  const photo = possibleItem ? store.primaryPhoto(possibleItem.id) : undefined;
  const source = { ...row.sourceValues, ...decision.values };
  const candidateSale = decision.createSale
    ? undefined
    : (row.saleSuggestions.find(
        (sale) => sale.id === (decision.saleID ?? row.saleID),
      ) ??
      (row.saleSuggestions.length === 1 ? row.saleSuggestions[0] : undefined));
  const toggle = (name: string, selected: boolean) =>
    setDecision((d) =>
      name === "item.quantity"
        ? { ...d, confirmStock: selected }
        : {
            ...d,
            replaceFields: selected
              ? [...new Set([...(d.replaceFields ?? []), name])]
              : d.replaceFields?.filter((f) => f !== name),
          },
    );
  return (
    <>
      <p className="migration-source-label">
        {row.sheet} · row {row.row}
        {row.sku ? ` · ${row.sku}` : ""}
      </p>
      <h2>{row.title}</h2>
      {row.issues.length > 0 && (
        <Alert tone="warning">
          <ul>
            {row.issues.map((issue) => (
              <li key={issue}>{issue}</li>
            ))}
          </ul>
        </Alert>
      )}
      {resolved ? (
        <>
          <p>
            Saved to your inventory. Download the report from the import
            receipt.
          </p>
          <Changes changes={row.receipt?.changes ?? []} />
        </>
      ) : (
        <fieldset className="migration-fields" disabled={busy}>
          <FieldGroup>
            <Field label="Inventory item">
              <SelectControl
                value={
                  decision.createItem
                    ? "new"
                    : (decision.itemID ??
                      row.itemID ??
                      (row.status === "ready" ? "new" : ""))
                }
                onValueChange={(value) =>
                  setDecision((d) => ({
                    ...d,
                    itemID: value && value !== "new" ? value : undefined,
                    createItem: value === "new",
                    saleID: undefined,
                    createSale: false,
                  }))
                }
                options={[
                  { value: "", label: <>Choose a match</> },
                  ...itemChoices.map((item) => ({
                    value: String(item.id),
                    label: (
                      <>
                        {item.title} · {item.sku} ·{" "}
                        {conditionLabels[item.condition]}
                        {item.size ? ` · ${item.size}` : ""} · {item.quantity}{" "}
                        available · {inventoryStatusLabels[item.status]}
                      </>
                    ),
                  })),
                  { value: "new", label: <>Create a separate new item</> },
                ]}
              />
            </Field>
          </FieldGroup>
          {possibleItem && (row.status === "attention" || decision.itemID) && (
            <MatchFacts
              kind="item"
              photo={photo?.thumbnailURL || photo?.url}
              rows={[
                ["Item", source.title, possibleItem.title],
                ["Stock code", source.sku, possibleItem.sku],
                [
                  "Condition",
                  source.condition,
                  conditionLabels[possibleItem.condition],
                ],
                ["Variant", source.size, possibleItem.size],
                ["Available", source.available, possibleItem.quantity],
                [
                  "Status",
                  source.status,
                  inventoryStatusLabels[possibleItem.status],
                ],
              ]}
            />
          )}
          <Disclosure label="Find another item">
            <Field label="Search all inventory">
              <Input value={query} onChange={(e) => setQuery(e.target.value)} />
            </Field>
            <Button
              variant="secondary"
              disabled={!query.trim() || busy || searching}
              onClick={() => {
                setSearching(true);
                setSearchError("");
                void store
                  .spreadsheetRequest<{
                    items: MigrationReviewRow["suggestions"];
                  }>({ action: "search", query })
                  .then((result) => setMatches(result.items))
                  .catch((e) => setSearchError(errorText(e)))
                  .finally(() => setSearching(false));
              }}
            >
              Search
            </Button>
            <small>Includes sold and archived items.</small>
            {searchError && <Alert tone="danger">{searchError}</Alert>}
            {query && (
              <div className="migration-search-results" aria-live="polite">
                {searching ? (
                  <p>Searching…</p>
                ) : matches.length ? (
                  matches.map((item) => (
                    <Button
                      key={item.id}
                      variant="ghost"
                      onClick={() =>
                        setDecision((d) => ({
                          ...d,
                          itemID: item.id,
                          createItem: false,
                          saleID: undefined,
                          createSale: false,
                        }))
                      }
                    >
                      {item.title} · {item.sku} ·{" "}
                      {conditionLabels[item.condition]}
                      {item.size ? ` · ${item.size}` : ""} · {item.quantity}{" "}
                      available · {inventoryStatusLabels[item.status]}
                    </Button>
                  ))
                ) : (
                  <p>No items match this search.</p>
                )}
              </div>
            )}
          </Disclosure>
          {row.saleSuggestions.length > 0 && (
            <>
              <Field label="Sale record">
                <SelectControl
                  value={
                    decision.createSale
                      ? "new"
                      : (decision.saleID ?? row.saleID ?? "")
                  }
                  onValueChange={(value) =>
                    setDecision((d) => ({
                      ...d,
                      saleID: value && value !== "new" ? value : undefined,
                      createSale: value === "new",
                    }))
                  }
                  options={[
                    { value: "", label: <>Choose a sale</> },
                    ...row.saleSuggestions.map((s) => ({
                      value: String(s.id),
                      label: <>{s.label}</>,
                    })),
                    { value: "new", label: <>A separate sale</> },
                  ]}
                />
              </Field>
              {candidateSale && (
                <MatchFacts
                  kind="sale"
                  rows={[
                    [
                      "Channel",
                      source.marketplace,
                      marketplaceLabels[candidateSale.marketplace],
                    ],
                    ["Date", source.sold_at, candidateSale.soldAt.slice(0, 10)],
                    [
                      "Amount",
                      row.values?.salePrice !== undefined
                        ? formatMoney({
                            amountMinor: row.values.salePrice,
                            currency,
                          })
                        : source.sale_price,
                      formatMoney(candidateSale.salePrice),
                    ],
                    ["Quantity", source.quantity, candidateSale.quantity ?? 1],
                    ["Order", source.order_id, candidateSale.externalOrderID],
                    [
                      "Order line",
                      source.line_id,
                      candidateSale.externalOrderLineItemID,
                    ],
                    [
                      "Status",
                      source.status,
                      saleStatusLabels[candidateSale.status],
                    ],
                  ]}
                />
              )}
            </>
          )}
          {row.changes.some((c) => c.conflict) && (
            <section className="migration-conflicts">
              <h3>Keep current values, or replace them</h3>
              {row.changes
                .filter((c) => c.conflict)
                .map((c) => (
                  <CheckboxField
                    key={c.field}
                    checked={
                      c.field === "item.quantity"
                        ? decision.confirmStock === true
                        : (decision.replaceFields?.includes(c.field) ?? false)
                    }
                    onChange={(e) => toggle(c.field, e.target.checked)}
                    label={
                      <>
                        <span>
                          <strong>{c.label}</strong>
                          <span className="migration-before-after">
                            {c.before}
                            <ArrowRight />
                            {c.after}
                          </span>
                        </span>
                      </>
                    }
                  />
                ))}
            </section>
          )}
          <Disclosure label="Source values">
            <dl className="migration-changes">
              {Object.entries(row.sourceValues)
                .filter(([, value]) => value)
                .map(([field, value]) => (
                  <div key={field}>
                    <dt>
                      {migrationFields.find(([key]) => key === field)?.[1]}
                    </dt>
                    <dd>{value}</dd>
                  </div>
                ))}
            </dl>
          </Disclosure>
          <Disclosure label="All proposed changes">
            <Changes changes={row.changes} />
          </Disclosure>
          <Disclosure label="Correct a spreadsheet value">
            <FieldGroup>
              <Field label="Column meaning">
                <SelectControl
                  value={field}
                  onValueChange={(value) => {
                    setField(value as MigrationField);
                    setValue(
                      decision.values?.[value as MigrationField] ??
                        row.sourceValues[value as MigrationField] ??
                        "",
                    );
                  }}
                  options={[
                    ...migrationFields
                      .filter(([key]) => key !== "ignore")
                      .map(([key, label]) => ({
                        value: String(key),
                        label: <>{label}</>,
                      })),
                  ]}
                />
              </Field>
              <Field label="Corrected value">
                {field === "status" ? (
                  <SelectControl
                    value={value}
                    onValueChange={(value) => setValue(value)}
                    options={[
                      { value: "", label: <>Choose status</> },
                      ...migrationStatuses.map(([key, label]) => ({
                        value: String(key),
                        label: <>{label}</>,
                      })),
                    ]}
                  />
                ) : (
                  <Input
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                  />
                )}
              </Field>
            </FieldGroup>
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() =>
                onSave({
                  ...decision,
                  values: { ...decision.values, [field]: value },
                })
              }
            >
              Recheck row
            </Button>
            {decision.values && (
              <small>
                {Object.keys(decision.values).length} corrections ready to save
              </small>
            )}
          </Disclosure>
          {dirty && (
            <Button
              variant="ghost"
              disabled={busy}
              onClick={() => {
                setDecision(row.decision);
                setValue(
                  row.decision.values?.[field] ?? row.sourceValues[field] ?? "",
                );
              }}
            >
              Discard choice changes
            </Button>
          )}
          <footer className="migration-row-actions">
            <Button
              variant="ghost"
              disabled={busy}
              onClick={() => onSave({ ...decision, skip: !decision.skip })}
            >
              {row.status === "skipped" ? "Include this row" : "Leave out"}
            </Button>
            <Button
              variant="primary"
              disabled={busy}
              onClick={() => onSave({ ...decision, skip: false })}
            >
              Save choice
              <ArrowRight />
            </Button>
          </footer>
        </fieldset>
      )}
    </>
  );
}
